/**
 * View component for /content-modules/:contentModuleId
 *
 * Displays a single contentModule from the 'byId' map in the contentModule reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, history, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import _ from 'lodash';

// import actions
import * as nodeActions from '../../node/nodeActions';
import * as sceneActions from '../../scene/sceneActions';
// todo: optimize calls and use search
import * as metricActions from '../../metric/metricActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as contentModuleActions from '../contentModuleActions';
import * as transitionActions from '../../transition/transitionActions';
import * as userEventActions from '../../userEvent/userEventActions';

// import utils
import adminUtils from '../../../global/utils/adminUtils';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Modal from '../../../global/components/modals/Modal.js.jsx';
import ContextMenu from '../../../global/components/helpers/ContextMenu.js.jsx';
// import ZendeskWidgetSettings from '../../../global/plugins/zendesk/ZendeskWidgetSettings.js.jsx';

// import contentModule components
import CMBuildLayout from '../components/CMBuildLayout.js.jsx';
import ContentModuleLayout from '../components/ContentModuleLayout.js.jsx';
import ContentModuleGraph from '../components/ContentModuleGraph.js.jsx';

// import components
import BuildSingleNodePreview from '../../node/components/BuildSingleNodePreview.js.jsx';
import NodeForm from '../../node/components/NodeForm.js.jsx';
import BuildSceneForm from '../../scene/components/BuildSceneForm.js.jsx';

// import contentModule css modules
import contentModuleStyles from '../contentModuleModuleStyles.css';
import { sendUpdateContentModule } from '../contentModuleActions';

class AdminBuildContentModule extends Base {
  constructor(props) {
    super(props);

    this.state = {
      alertMessage: ""
      , isDeleteNodeModalOpen: false
      , isNewChartContent: true
      , isNewDescriptionContent: true
      , isNewTranscriptContent: true
      , isUnlinkSceneModalOpen: false
      , newScene: _.cloneDeep(props.sceneStore.defaultItem)
      , selectedNode: null
      , showNewSceneModal: false
      , showEditNodeModal: false
      , showEditSceneModal: false
      , showUpdateNodeForm: false
      , editingNode: null
      , editingScene: null
      , sceneMediaType: null

    }

    this._bind(
      // node handlers
      '_closeDeleteNodeModal'
      , '_closeCreateNewSceneModal'
      , '_closeEditSceneModal'
      , '_closeUpdateNodeModal'
      , '_handleNodeDeselect'
      , '_handleNodeDragEnd'
      , '_handleNodeDragStart'
      , '_handleNodeFormChange'
      , '_handleNodeFormSubmit'
      , '_handleNodeSelect'
      , '_handleDeleteNode'
      , '_handleUnlinkScene'
      , '_openModal'
      , '_toggleShowUpdateNodeForm'

      // scene handlers
      , '_addSceneTrack'
      , '_closeUnlinkSceneModal'
      , '_deleteTrack'
      , '_handleEditSceneFormChange'
      , '_handleEditSceneFormSubmit'
      , '_handleNewSceneFormChange'
      , '_handleNewSceneFormSubmit'
      , '_handleTrackFormChange'

      // transition handlers
      , '_handleNewTransition'
      , '_handleRemoveTransition'

      // graph handlers
      , '_handleGraphPanEnd'
      , '_handleGraphScale'
      , '_handleNewNode'
      , '_resetPanAndScale'
      , '_getGraphParamsCenteredOnPoint'
      , '_getPointCenteredInView'

      // other handlers
      , '_handleExportNodeCsv'
      , '_handleApplyLayoutToNodes'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;

    // 1.14 send user access tracking
    dispatch(userEventActions.sendCreateUserEvent({
      eventType: "user-access"
      , eventAction: "admin-content-module-build"
      , _contentModule: match.params.contentModuleId
    }));

    dispatch(metricActions.fetchListIfNeeded('all')) // todo: optimize and use search

    dispatch(contentModuleActions.fetchSingleIfNeeded(match.params.contentModuleId)).then((action) => {
      if(action.success) {
        this.setState({ graphParams: { ...action.item.graphParams }})
      } else {
        alert("ERROR LOADING TRAINING MODILE")
      }
    });
    dispatch(sceneActions.fetchListIfNeeded('contentModule', match.params.contentModuleId));

    dispatch(sceneActions.fetchListIfNeeded('all'));
    dispatch(transitionActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId));
  }

  componentWillReceiveProps(nextProps) {
    const { dispatch, match } = this.props;
    dispatch(transitionActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId));
    dispatch(sceneActions.fetchListIfNeeded('contentModule', match.params.contentModuleId));
    dispatch(nodeActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId)).then((nodeAction) => {
      // let sceneIds = nodeAction.list && nodeAction.list.length > 0 ? nodeAction.list.map(node => node._scene) : [];
      // dispatch(sceneActions.fetchListIfNeeded("_id", sceneIds));
      if(nodeAction.list.length === 0) {
        return;
      }
      const urlSearchParams = new URLSearchParams(window.location.search);
      // console.log({windowLocation: window.location})
      const selectedNodeId = urlSearchParams.get('selectedNode');
      // console.log({ nodeList: nodeAction, selectedNodeId })
      if(selectedNodeId && (!this.state.selectedNode || selectedNodeId !== this.state.selectedNode._id)) {
        const selectedNode = nodeAction.list.find(node => node._id === selectedNodeId);
        window.history.replaceState({}, '', `${window.location.pathname}`);
        if(selectedNode) {
          const newGraphParams = this._getGraphParamsCenteredOnPoint(selectedNode.authoring, this.state.graphParams);
          const newX = newGraphParams.xOffset;
          const newY = newGraphParams.yOffset;
          if(this.state.graphParams.xOffset === newX && this.state.graphParams.yOffset === newY) {
            return;
          }
          this.setState({ graphParams: newGraphParams }, () => {
            this._handleNodeSelect(selectedNode);
          });
        }
      }
    });
  }

  _getGraphParamsCenteredOnPoint(point = {x: 0, y: 0}, graphParams = {width: 0, height: 0, scale: 1}) {
    const positionPercentageX = point.x / graphParams.width;
    const positionPercentageY = point.y / graphParams.height;

    const scaledWidth = graphParams.width * (graphParams.scale * .1);
    const scaledHeight = graphParams.height * (graphParams.scale * .1);
    const centerPoint = -(graphParams.width * .5)

    const minX = centerPoint + (scaledWidth * .5);
    const maxX = centerPoint - (scaledWidth * .5);
    const minY = centerPoint + (scaledHeight * .5);
    const maxY = centerPoint - (scaledHeight * .5);

    const viewPortWidth = window.innerWidth;
    const viewPortHeight = window.innerHeight;

    // add some small offsets based on viewport to put it in the center and not the top left
    const newX = Math.floor(minX + (positionPercentageX * (maxX - minX)) + (viewPortWidth * .5));
    const newY = Math.floor(minY + (positionPercentageY * (maxY - minY)) + (viewPortHeight * .5));
  
    return { ...graphParams, xOffset: newX, yOffset: newY}
  }

  _getPointCenteredInView(graphParams = {width: 0, height: 0, scale: 1, xOffset: 0, yOffset: 0}) {
    const viewPortWidth = window.innerWidth;
    const viewPortHeight = window.innerHeight;
  
    const scaledWidth = graphParams.width * (graphParams.scale * .1);
    const scaledHeight = graphParams.height * (graphParams.scale * .1);
    const centerPoint = -(graphParams.width * .5)
  
    const minX = centerPoint + (scaledWidth * .5);
    const maxX = centerPoint - (scaledWidth * .5);
    const minY = centerPoint + (scaledHeight * .5);
    const maxY = centerPoint - (scaledHeight * .5);
  
    const positionPercentageX = (graphParams.xOffset - minX - (viewPortWidth * .5)) / (maxX - minX);
    const positionPercentageY = (graphParams.yOffset - minY - (viewPortHeight * .5)) / (maxY - minY);
  
    const x = positionPercentageX * graphParams.width;
    const y = positionPercentageY * graphParams.height;
  
    return { x, y };
  }

  _addSceneTrack(formType) {
    let currentTrackList = formType === 'new' ? this.state.newScene.tracks : this.state.editingScene.tracks;
    currentTrackList.push({ file: '', label: '' });
    if(formType === 'new') {

      this._handleNewSceneFormChange({
        target: {
          name: "tracks"
          , value: currentTrackList
        }
      });
    } else {
      this._handleEditSceneFormChange({
        target: {
          name: "tracks"
          , value: currentTrackList
        }
      });
    }
  }

  _deleteTrack(index, formType) {
    let nextTrackList = formType === 'new' ? this.state.newScene.tracks : this.state.editingScene.tracks;
    nextTrackList.splice(index, 1);
    if(formType === 'new') {

      this._handleNewSceneFormChange({
        target: {
          name: "tracks"
          , value: nextTrackList
        }
      });
    } else {
      this._handleEditSceneFormChange({
        target: {
          name: "tracks"
          , value: nextTrackList
        }
      });
    }
  }

  _handleTrackFormChange(e, index, formType) {
    console.log(this.state);
    let nextTrackList = formType === 'new' ? this.state.newScene.tracks : this.state.editingScene.tracks;
    if(e.name && e.value) {
      nextTrackList[index][e.name] = e.value;
    } else {
      nextTrackList[index][e.target.name] = e.target.value;
    }
    if(formType === 'new') {

      this._handleNewSceneFormChange({
        target: {
          name: "tracks"
          , value: nextTrackList
        }
      });
    } else {
      this._handleEditSceneFormChange({
        target: {
          name: "tracks"
          , value: nextTrackList
        }
      });
    }
  }

  _closeCreateNewSceneModal() {
    this.setState({
      newScene: _.cloneDeep(this.props.sceneStore.defaultItem)
      , showNewSceneModal: false
    })
  }

  _closeEditSceneModal() {
    this.setState({
      editingScene: null
      , showEditSceneModal: false
    })
  }

  _toggleShowUpdateNodeForm() {
    this.setState({
      showUpdateNodeForm: !this.state.showUpdateNodeForm
    });
  }

  _handleNodeFormChange(e) {
    let newNodeState = _.update(this.state.editingNode, e.target.name, () => {
      return e.target.value;
    });
    this.setState({ editingNode: newNodeState });
  }

  _handleNodeFormSubmit() {
    const { dispatch } = this.props;
    const { editingNode } = this.state;
    dispatch(nodeActions.sendUpdateNode(editingNode)).then((action) => {
      if(action.success) {
        // select scene for this node
        if(action.item._scene) {
          dispatch(sceneActions.fetchSingleIfNeeded(action.item._scene));
        }
        this.setState({ selectedNode: action.item });
        // hide update form
        this._closeUpdateNodeModal();
      } else {
        // console.log("Response Error:");
        // console.log(action);
        alert(`ERROR: ${action.error}`);
      }
    });
  }

  _handleUnlinkScene() {
    const { dispatch } = this.props;
    let newNode = _.cloneDeep(this.state.selectedNode);
    newNode._scene = null;
    dispatch(nodeActions.sendUpdateNode(newNode)).then((action) => {
      dispatch(sceneActions.invalidateSelected());
    });
    this._closeUnlinkSceneModal();
  }

  _handleEditSceneFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */
    // console.log('_handleEditSceneFormChange');
    let newSceneState = _.update(this.state.editingScene, e.target.name, function () {
      return e.target.value;
    });
    // console.log(newSceneState);
    // RichText content
    if(e.target.name === "chart") {
      this.setState({
        isNewChartContent: false
      });
    }
    if(e.target.name === "description") {
      this.setState({
        isNewDescriptionContent: false
      });
    }
    if(e.target.name === "transcript") {
      this.setState({
        isNewTranscriptContent: false
      });
    }
    // helpers
    if(e.target.name === "sceneMediaType") {
      this.setState({ sceneMediaType: e.target.value });
    }

    // Scenes should have a video OR a pdf. Never both.
    if(e.target.name === "videoPath" && e.target.value) {
      newSceneState.pdfPath = ''
    }
    if(e.target.name === "pdfPath" && e.target.value) {
      newSceneState.videoPath = ''
    }
    this.setState({ editingScene: newSceneState });
  }

  _handleEditSceneFormSubmit() {
    const { dispatch, sceneStore } = this.props;
    const { editingScene, selectedNode, sceneMediaType } = this.state;
    let scene = _.cloneDeep(editingScene);
    if(!sceneMediaType) {
      scene.videoPath = '';
      scene.pdfPath = '';
    }
    dispatch(sceneActions.sendUpdateScene(scene)).then((sceneAction) => {
      if(sceneAction.success) {
        // set our new state
        let newNode = _.cloneDeep(selectedNode);
        newNode._scene = sceneAction.item._id;
        dispatch(nodeActions.sendUpdateNode(newNode)).then((nodeAction) => {
          if(nodeAction.success) {
            this.setState({
              ...this.state
              , isNewChartContent: true
              , isNewDescriptionContent: true
              , isNewTranscriptContent: true
              , editingScene: sceneAction.item
            });
            // add to list
            // toggle new scene modal
            this._closeEditSceneModal();
          } else {
            alert(`ERROR with node update - ${nodeAction.error}`);

          }
        })
      } else {
        // console.log("Response Error:");
        // console.log(action);
        alert(`ERROR with scene creation - ${sceneAction.error}`);
      }
    });
  }

  _handleNewSceneFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */
    let newSceneState = _.update(this.state.newScene, e.target.name, function () {
      return e.target.value;
    });
    // RichText content
    if(e.target.name === "description") {
      this.setState({
        isNewDescriptionContent: false
      });
    }
    if(e.target.name === "chart") {
      this.setState({
        isNewChartContent: false
      });
    }
    if(e.target.name === "transcript") {
      this.setState({
        isNewTranscriptContent: false
      });
    }
    // helpers
    if(e.target.name === "sceneMediaType") {
      this.setState({ sceneMediaType: e.target.value });
    }
    // Scenes should have a video OR a pdf. Never both.
    if(e.target.name === "videoPath" && e.target.value) {
      newSceneState.pdfPath = ''
    }
    if(e.target.name === "pdfPath" && e.target.value) {
      newSceneState.videoPath = ''
    }
    this.setState({ newScene: newSceneState });
  }

  _handleNewSceneFormSubmit() {
    const { dispatch, sceneStore } = this.props;
    const { newScene, selectedNode, sceneMediaType } = this.state;
    let scene = _.cloneDeep(newScene);
    if(!sceneMediaType) {
      scene.videoPath = '';
      scene.pdfPath = '';
    }
    dispatch(sceneActions.sendCreateScene(scene)).then((sceneAction) => {
      if(sceneAction.success) {
        // set our new state
        dispatch(sceneActions.addSceneToList(sceneAction.item._id, "all"));
        let newNode = _.cloneDeep(selectedNode);
        newNode._scene = sceneAction.item._id;
        dispatch(nodeActions.sendUpdateNode(newNode)).then((nodeAction) => {
          if(nodeAction.success) {
            this.setState({
              ...this.state
              , isNewChartContent: true
              , isNewDescriptionContent: true
              , isNewTranscriptContent: true
              , newScene: _.cloneDeep(sceneStore.defaultItem)
              , selectedNode: nodeAction.item
            });
            // add to list
            // toggle new scene modal
            this._closeCreateNewSceneModal();
          } else {
            alert(`ERROR with node update - ${nodeAction.error}`);

          }
        })
      } else {
        console.log("Response Error:");
        // console.log(sceneAction);
        alert(`ERROR with scene creation - ${sceneAction.error}`);
      }
    });
  }

  // node handlers
  _handleNodeDragStart(node) {
    console.log("BUILD PAGE - handle drag start") // unused currently
  }

  _openModal(modal) {
    if(modal === 'deleteNode') {
      this.setState({
        alertMessage: "Are you sure you want to permanently delete this Node? The associated Scene, if any, will still exist in the database."
        , isDeleteNodeModalOpen: true
      });
    } else if(modal === 'newScene') {
      this.setState({
        sceneMediaType: ''
        , showNewSceneModal: true
      })
    } else if(modal === 'editNode') {
      this.setState({
        showEditNodeModal: true
        , editingNode: _.cloneDeep(this.state.selectedNode)
      })
    } else if(modal === 'editScene') {
      let editingScene = _.cloneDeep(this.props.sceneStore.selected.getItem());
      this.setState({
        showEditSceneModal: true
        , editingScene
        , sceneMediaType: editingScene.pdfPath ? 'pdf' : editingScene.videoPath ? 'video' : ''
      })
    } else if(modal === 'unlinkScene') {
      this.setState({
        alertMessage: "Are you sure you want to unlink this Scene?"
        , isUnlinkSceneModalOpen: true
      })
    }
  }

  _closeUpdateNodeModal() {
    this.setState({
      showEditNodeModal: false
      , editingNode: null
    })
  }

  _handleNodeDragEnd(node, x, y) {
    // console.log("BUILD PAGE - handle drag end")
    if(node.authoring.x !== x || node.authoring.y !== y) {
      const newNode = {
        ...node
        , authoring: {
          ...node.authoring
          , x
          , y
        }
      }
      this.props.dispatch(nodeActions.sendUpdateNode(newNode))
    } //else: we don't need to update the node
  }

  _handleNodeSelect(node) {
    // display in modal or something for editing
    // console.log("BUILD PAGE - node selected");
    // select scene for this node
    // TODO: make the selected node happen in the reducer
    const { dispatch } = this.props;
    dispatch(sceneActions.fetchSingleIfNeeded(node._scene));
    // update node
    this.setState({  selectedNode: node });
  }

  _handleNodeDeselect() {
    // hide edit form or something
    // console.log("BUILD PAGE - node deselected")
    this.setState({ selectedNode: null })
  }

  _handleDeleteNode(nodeId) {
    const { dispatch, match } = this.props;
    if(nodeId) {
      this.setState({ deletingNode: true });
      // so, we want to remove it from the selected list BEFORE we delete it and remove it from the map
      dispatch(nodeActions.removeNodeFromList(nodeId, '_contentModule', match.params.contentModuleId))
      dispatch(nodeActions.sendDelete(nodeId)).then((action) => {
        // console.log("DELETED NODE AND ITS TRANSITIONS FROM DB");
        // console.log(action);
        dispatch(transitionActions.invalidateList('_contentModule', match.params.contentModuleId));
        this.setState({ deletingNode: false });
      });
      this.setState({
        selectedNode: null
      });
      this._closeDeleteNodeModal();
    }
  }

  // transition handlers
  _handleNewTransition(transition) {
    let newTransition = {
      ...transition
      , _contentModule: this.props.match.params.contentModuleId
      , _createdBy: this.props.loggedInUser._id
    }
    if(newTransition._startNode == "" + newTransition._endNode) {
      return;
    } else {
      this.props.dispatch(transitionActions.sendCreateTransition(newTransition)).then((action) => {
        if(action.success) {
          // add it to the map and list (this will refresh the graph props without having to refetch the entire list)
          this.props.dispatch(transitionActions.addSingleTransitionToMap(action.item))
          this.props.dispatch(transitionActions.addTransitionToList(action.item._id, '_contentModule', this.props.match.params.contentModuleId))
        } else {
          console.log(action);
          alert("FAILURE TO CREATE TRANSITION");
        }
      })
    }
  }

  _handleRemoveTransition(transition) {
    // console.log("HANLDE REMOVE", transition)
    this.props.dispatch(transitionActions.removeTransitionFromList(transition._id, '_contentModule', this.props.match.params.contentModuleId))
    this.props.dispatch(transitionActions.sendDelete(transition._id));
  }

  // graph handlers
  _handleNewNode(x, y) {
    // on double click on the graph, create new node
    // double click on the NODE handles selected, and we prevent this from firing too with preventPropagation and preventDefault
    // console.log("BUILD PAGE - graph double click")
    // console.log(x, y);

    // get contentModule
    const selectedContentModule = this.props.contentModuleStore.selected.getItem();
    if(!selectedContentModule) {
      console.log("ERROR, not content module in store");
      return;
    } else {
      let newNode = {
        ...this.props.nodeStore.defaultItem // copy default item
        , _contentModule: selectedContentModule._id
        , _createdBy: this.props.loggedInUser._id
        , authoring: {
          ...this.props.nodeStore.defaultItem.authoring
          , x
          , y
        }
      }
      // console.log(newNode);
      this.props.dispatch(nodeActions.sendCreateNode(newNode)).then((action) => {
        if(action.success) {
          // add it to the map and list (this will refresh the graph props without having to refetch the entire list)
          this.props.dispatch(nodeActions.addSingleNodeToMap(action.item))
          this.props.dispatch(nodeActions.addNodeToList(action.item._id, '_contentModule', selectedContentModule._id))
        } else {
          console.log(action);
          alert("FAILURE TO CREATE NODE");
        }
      })
    }

  }

  _handleGraphPanEnd(x, y) {
    // console.log("GRAPH PAN END", x, y)
    if(this.state.graphParams.xOffset !== x || this.state.graphParams.yOffset !== y) {
      const nextGraphParams = {
        ...this.state.graphParams
        , xOffset: x
        , yOffset: y
      }
      this.setState({
        graphParams: nextGraphParams
      })
      // update content module object
      const selectedContentModule = this.props.contentModuleStore.selected.getItem();
      const updatedContentModule = {
        ...selectedContentModule
        , graphParams: nextGraphParams
      }
      // save to db
      this.props.dispatch(sendUpdateContentModule(updatedContentModule));
      // ^ this will update the selected object in place so that we don't have to re-fetch afterwards
    } // else: didn't move, so no need to update
  }

  _handleGraphScale(scale) {
    // using 0-20 scale for now, default is 10. to actually zoom we do a scale transform to 10/10 = 1, or 5/10 = 0.5, etc
    console.log("graph scale to " + scale);
    if(scale < 1 || scale > 20) {
      return; // arbitrary
    }
    
    let nextGraphParams = {
      ...this.state.graphParams
      , scale: scale
    }
    // we want to zoom relative to the current center of the screen
    nextGraphParams = this._getGraphParamsCenteredOnPoint(this._getPointCenteredInView(this.state.graphParams), nextGraphParams);
    // if we have a selected node, we want to zoom in on that node
    if (this.state.selectedNode) {
      nextGraphParams = this._getGraphParamsCenteredOnPoint(this.state.selectedNode.authoring, { ...this.state.graphParams, scale: scale });
    } 

    this.setState({
      graphParams: nextGraphParams
    })

    // update content module object
    const selectedContentModule = this.props.contentModuleStore.selected.getItem();
    const updatedContentModule = {
      ...selectedContentModule
      , graphParams: nextGraphParams
    }
    // save to db
    this.props.dispatch(sendUpdateContentModule(updatedContentModule));
  }

  _resetPanAndScale() {
    const newGraphParams = this._getGraphParamsCenteredOnPoint({ x: 10600, y: 10200 }, { ...this.state.graphParams, scale: 9 });
    this.setState({
      graphParams: newGraphParams
    })
  }

  _closeDeleteNodeModal() {
    this.setState({
      alertMessage: ""
      , isDeleteNodeModalOpen: false
    });
  }

  _closeUnlinkSceneModal() {
    this.setState({
      alertMessage: ""
      , isUnlinkSceneModalOpen: false
    });
  }

  _handleExportNodeCsv() {
    const { nodeStore, sceneStore, contentModuleStore, match } = this.props;
    const selectedContentModule = contentModuleStore.selected.getItem();
    const nodes = nodeStore.util.getList('_contentModule', match.params.contentModuleId);
    const scenes = sceneStore.util.getList('contentModule', match.params.contentModuleId);
    console.log("EXPORT NODE CSV");
    let csv = adminUtils.generateModuleNodesTableViewExport(nodes, scenes);
    const filename = `${selectedContentModule.title}_NODES_TABLE_VIEW_${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}.csv`
    // download as a file when the user clicks the button
    csv = 'data:text/csv;charset=utf-8,' + csv;
    let data = encodeURI(csv);
    let link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }

  _handleApplyLayoutToNodes() {
    const { dispatch, match } = this.props;
    // use a popup to confirm
    if(window.confirm("Are you sure you want to apply auto-layout to all nodes in this module? This will overwrite any existing node positions.")) {
      dispatch(nodeActions.applyLayoutToModule(match.params.contentModuleId)).then((action) => {
        if (action.success) {
          console.log("APPLIED LAYOUT TO NODES");
          console.log(action);
        } else {
          console.log("ERROR APPLYING LAYOUT TO NODES", action);
          // alert("ERROR APPLYING LAYOUT TO NODES", action.message);
          alert(action.error);
        }
      });
    }
  }

  render() {
    const {
      metricStore
      , nodeStore
      , sceneStore
      , contentModuleStore
      , transitionStore
    } = this.props;
    // console.log('render entire build page')
    const {
      alertMessage
      , newScene
      , isDeleteNodeModalOpen
      , isNewChartContent
      , isNewDescriptionContent
      , isNewTranscriptContent
      , isUnlinkSceneModalOpen
      , selectedNode
      , showNewSceneModal
      , showEditSceneModal
      , showEditNodeModal
    } = this.state;

    const selectedContentModule = this.props.contentModuleStore.selected.getItem();
    const nodesArray = nodeStore.util.getList('_contentModule', this.props.match.params.contentModuleId);
    const transitionsArray = transitionStore.util.getList('_contentModule', this.props.match.params.contentModuleId);
    const selectedScene = sceneStore.selected.getItem();

    // todo: optimize for search. keeping very simpler now so I can get to testing backend parts.
    const allMetrics = metricStore.util.getList('all')

    const isEmpty = !selectedContentModule || !selectedContentModule._id || !this.state.graphParams

    return (
      <CMBuildLayout>
        <Helmet
          title={"Create Module | Pro-ficiency"}
        />

        { !isEmpty ?
          <div className="-build-content" style={{ opacity: contentModuleStore.isFetching ? 0.5 : 1 }}>
            <ContentModuleGraph
              graphParams={this.state.graphParams}
              handleGraphDoubleClick={this._handleNewNode}
              handleGraphPanEnd={this._handleGraphPanEnd}
              handleGraphScale={this._handleGraphScale}
              handleNewTransition={this._handleNewTransition}
              handleNodeDeselect={this._handleNodeDeselect}
              handleNodeDragEnd={this._handleNodeDragEnd}
              handleNodeDragStart={this._handleNodeDragStart}
              handleNodeSelect={this._handleNodeSelect}
              handleOpenModal={this._openModal}
              handleRemoveTransition={this._handleRemoveTransition}
              handleUnlinkScene={this._handleUnlinkScene}
              nodes={nodesArray}
              resetPanAndScale={this._resetPanAndScale}
              selectedNode={selectedNode}
              selectedScene={selectedScene}
              selectedContentModuleId={selectedContentModule._id}
              transitions={transitionsArray}
            />
            <ContextMenu>
              <ContextMenu.MenuItem onClick={this._handleExportNodeCsv}>Export module nodes CSV</ContextMenu.MenuItem>
              <ContextMenu.MenuItem onClick={this._handleApplyLayoutToNodes}>Apply layout to nodes</ContextMenu.MenuItem>
            </ContextMenu>
          {/**
              selectedNode && selectedNode._id ?
              <div>
                <h3 className="u-centerText"> SELECTED NODE: {selectedNode._id}
                  <button
                    className="yt-btn small"
                    onClick={this._toggleShowUpdateNodeForm}
                    style={{marginLeft:"20px"}}
                    type="button"
                  >
                    { showUpdateNodeForm ? "CANCEL CHANGES" : "UPDATE NODE" }
                  </button>
                  { !selectedNode.isStartNode ?
                    <button
                      className="yt-btn small danger"
                      type="button"
                      style={{marginLeft:"20px"}}
                      }
                    >
                      DELETE NODE
                    </button>
                    :
                    null
                  }
                </h3>
                { showUpdateNodeForm ?
                  <NodeForm
                    formTitle="Update Node"
                    formType="update"
                    graphTypes={nodeStore.defaultGraphTypes}
                    handleFormChange={this._handleNodeFormChange}
                    handleFormSubmit={this._handleNodeFormSubmit}
                    node={selectedNode}
                    sceneStore={sceneStore}
                    toggleCreateNewScene={this._toggleCreateNewSceneModal}
                  />
                  :
                  <BuildSingleNodePreview
                    node={selectedNode}
                    scene={selectedScene}
                  />
                }
              </div>
              :
              null
              // <h3 className="u-centerText">Please Select a Node to Edit (Double Click)</h3>
            */}
          </div>
          :
          <div className="u-centerText">
            <div className="loading"></div>
          </div>
        }
        {/* TODO: create Metric modal */}
        <Modal
          btnColor="info"
          cardSize={"large"}
          closeAction={() => this._closeCreateNewSceneModal()}
          closeText="Cancel"
          confirmAction={() => this._handleNewSceneFormSubmit()}
          confirmText="Save & Close"
          isOpen={showNewSceneModal}
          modalClasses="info"
          modalHeader={<span>Create a new scene</span>}
          showTopButtons={true}
        >
          <BuildSceneForm
            addSceneTrack={this._addSceneTrack}
            deleteTrack={this._deleteTrack}
            formType="new"
            handleFormChange={this._handleNewSceneFormChange}
            handleTrackFormChange={this._handleTrackFormChange}
            sceneMediaType={this.state.sceneMediaType}
            isNewChartContent={isNewChartContent}
            isNewDescriptionContent={isNewDescriptionContent}
            isNewTranscriptContent={isNewTranscriptContent}
            scene={newScene}
          />
        </Modal>
        <Modal
          btnColor="info"
          cardSize={"large"}
          closeAction={() => this._closeEditSceneModal()}
          closeText="Cancel"
          confirmAction={() => this._handleEditSceneFormSubmit()}
          confirmText="Save & Close"
          isOpen={showEditSceneModal}
          modalClasses="info"
          modalHeader={<span>Edit this Scene</span>}
          showTopButtons={true}
        >
          <BuildSceneForm
            addSceneTrack={this._addSceneTrack}
            deleteTrack={this._deleteTrack}
            formType="edit"
            handleFormChange={this._handleEditSceneFormChange}
            handleTrackFormChange={this._handleTrackFormChange}
            sceneMediaType={this.state.sceneMediaType}
            isNewChartContent={isNewChartContent}
            isNewDescriptionContent={isNewDescriptionContent}
            isNewTranscriptContent={isNewTranscriptContent}
            scene={this.state.editingScene}
          />
        </Modal>
        <Modal
          btnColor="info"
          cardSize={"large"}
          closeAction={() => this._closeUpdateNodeModal()}
          closeText="Cancel"
          confirmAction={() => this._handleNodeFormSubmit()}
          confirmText="Save & Close"
          isOpen={showEditNodeModal}
          modalClasses="info"
          modalHeader={<span>Edit this node</span>}
        >
          <NodeForm
            handleFormChange={this._handleNodeFormChange}
            handleFormSubmit={this._handleNodeFormSubmit}
            node={this.state.editingNode}
            // trackingMetrics={isEmpty ? [] : selectedContentModule.metrics}
            allMetrics={allMetrics}
          />
        </Modal>
        <AlertModal
          alertMessage={<div>
            <strong>
              {alertMessage}
            </strong>
          </div>}
          alertTitle="WARNING!"
          closeAction={this._closeDeleteNodeModal}
          confirmAction={() => this._handleDeleteNode(selectedNode ? selectedNode._id : null)}
          confirmText="YES, DELETE"
          declineText="CANCEL"
          isOpen={isDeleteNodeModalOpen}
          type="danger"
        />
        <AlertModal
          alertMessage={<div>
            <strong>
              {alertMessage}
            </strong>
          </div>}
          alertTitle="WARNING!"
          closeAction={this._closeUnlinkSceneModal}
          confirmAction={() => this._handleUnlinkScene()}
          confirmText="YES, UNLINK"
          declineText="CANCEL"
          isOpen={isUnlinkSceneModalOpen}
          type="danger"
        />
        {/* <ZendeskWidgetSettings hideLauncher={true} /> */}
      </CMBuildLayout>
    )
  }
}

AdminBuildContentModule.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    loggedInUser: store.user.loggedIn.user
    , metricStore: store.metric
    , nodeStore: store.node
    , sceneStore: store.scene
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleStore: store.contentModule
    , transitionStore: store.transition
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminBuildContentModule)
);



// WEBPACK FOOTER //
// modules/contentModule/views/AdminBuildContentModule.js.jsx